// import { useEffect, useRef } from "react";

// /**
//  * Per-<th> style overrides, applied on top of the defaults.
//  * Anything you don't set falls back to the hook's built-in styling.
//  */
// export interface StickyHeaderThStyleOverrides {
//   color?: string;
//   fontWeight?: string;
//   fontSize?: string;
//   borderColor?: string;
//   backgroundColor?: string;
//   padding?: string;
// }

// export interface UseStickyDataTableHeaderOptions {
//   /**
//    * Height (px) of any fixed navbar above the table. The cloned header
//    * sticks to `top: navbarHeight`. Default: 112.
//    */
//   navbarHeight?: number;

//   /**
//    * CSS selector(s) (joined internally with `,`) used to identify <th>
//    * elements that should stay pinned/frozen when scrolling horizontally.
//    * Default matches PrimeReact's frozen-column + this codebase's
//    * "selectionHeader" convention: ".p-frozen-column, .selectionHeader"
//    */
//   frozenColumnSelector?: string;

//   /**
//    * Selector for the selection/checkbox header cell, if this table has
//    * a selection column. Pass `null`/omit if there's no checkbox column
//    * — the hook will simply skip all selection-sync behavior.
//    * Default: ".selectionHeader"
//    */
//   selectionHeaderSelector?: string | null;

//   /** Width (px) reserved for the selection header cell. Default: 35. */
//   selectionColumnWidth?: number;

//   /** Max px width before a column header truncates with an ellipsis + tooltip. Default: 120. */
//   headerTitleMaxWidth?: number;

//   /** Style overrides merged on top of the hook's defaults for every cloned <th>. */
//   thStyleOverrides?: StickyHeaderThStyleOverrides;

//   /**
//    * If false, the hook does nothing (no listeners, no clone). Useful for
//    * pages where the sticky behavior should be conditionally disabled.
//    * Default: true.
//    */
//   enabled?: boolean;

//   /**
//    * Selector for sortable header cells, used to forward clicks from the
//    * cloned header to the original (hidden) header so PrimeReact's real
//    * onSort handler fires. Default matches PrimeReact's own markup:
//    * ".p-sortable-column"
//    */
//   sortableColumnSelector?: string;
// }

// const DEFAULT_FROZEN_SELECTOR = ".p-frozen-column, .selectionHeader";
// const DEFAULT_SELECTION_SELECTOR = ".selectionHeader";
// const DEFAULT_NAVBAR_HEIGHT = 112;
// const DEFAULT_HEADER_TITLE_MAX_WIDTH = 120;
// const DEFAULT_SORTABLE_SELECTOR = ".p-sortable-column";

// /**
//  * Clones a PrimeReact DataTable's <thead> into a `position: fixed` header
//  * that stays visible while the table's body scrolls past the navbar,
//  * preserving frozen/sticky columns, the selection-checkbox visuals, and
//  * click-to-sort behavior.
//  *
//  * Attach the returned `containerRef` to the element that wraps the
//  * PrimeReact <DataTable />. Re-runs whenever `watchDeps` changes (pass
//  * things like `columns` and the table's row data so the clone is rebuilt
//  * after structural changes).
//  *
//  * All DOM class names this hook looks for (`.p-datatable-table`,
//  * `.p-datatable-thead`, `.p-datatable-wrapper`, `.p-datatable-tbody`,
//  * `.p-checkbox`, `.p-checkbox-box`, `.p-highlight`, `.p-column-title`,
//  * `.p-column-header-content`, `.p-sortable-column`, `.p-sortable-column-icon`)
//  * come from PrimeReact's own DataTable markup, so this hook is safe to
//  * reuse on any PrimeReact DataTable, not just this one.
//  *
//  * Sorting works by forwarding clicks: the clone is a `cloneNode(true)` of
//  * the original `<thead>`, which copies markup/classes but NOT React's
//  * attached event listeners. So clicking a cloned sortable header dispatches
//  * a synthetic click on the matching cell in the original (hidden) header,
//  * letting PrimeReact's real `onSort` handler run. The resulting sort-icon /
//  * `aria-sort` changes on the original are then mirrored back onto the
//  * clone by the same MutationObserver that already powers the structural
//  * refresh.
//  */
// export function useStickyDataTableHeader<
//   T extends HTMLElement = HTMLDivElement,
// >(
//   watchDeps: React.DependencyList,
//   options: UseStickyDataTableHeaderOptions = {},
// ) {
//   const containerRef = useRef<T>(null);

//   const {
//     navbarHeight = DEFAULT_NAVBAR_HEIGHT,
//     frozenColumnSelector = DEFAULT_FROZEN_SELECTOR,
//     selectionHeaderSelector = DEFAULT_SELECTION_SELECTOR,
//     selectionColumnWidth = 35,
//     headerTitleMaxWidth = DEFAULT_HEADER_TITLE_MAX_WIDTH,
//     thStyleOverrides = {},
//     enabled = true,
//     sortableColumnSelector = DEFAULT_SORTABLE_SELECTOR,
//   } = options;

//   useEffect(() => {
//     if (!enabled) return;

//     let stickyTable: HTMLElement | null = null;

//     // ── Concurrency guard ────────────────────────────────────────────────
//     // Multiple triggers (scroll, resize, structural mutation, checkbox
//     // class mutation) can each ask to rebuild the sticky header in a tight
//     // window. createStickyHeader()/removeSticky() are not safe to interleave
//     // — a second call tearing down the DOM node while a first call is still
//     // populating cloneThs is what produces a blank/empty cloned header.
//     // This flag makes sure only one rebuild runs at a time.
//     let isRebuilding = false;

//     const isFrozenTh = (th: HTMLElement) => th.matches(frozenColumnSelector);
//     const isSortableTh = (th: HTMLElement) =>
//       sortableColumnSelector ? th.matches(sortableColumnSelector) : false;

//     const syncHorizontalScroll = () => {
//       if (!stickyTable || !containerRef.current) return;
//       const wrapper = containerRef.current.querySelector(
//         ".p-datatable-wrapper",
//       ) as HTMLElement;
//       const clipWrapper = stickyTable.querySelector(
//         "[data-clip-wrapper]",
//       ) as HTMLElement;
//       const cloneTable = stickyTable.querySelector("table") as HTMLElement;
//       if (!wrapper || !cloneTable || !clipWrapper) return;

//       const scrollLeft = wrapper.scrollLeft;

//       // Move the clip wrapper left (clips non-frozen columns at frozen boundary)
//       // Frozen columns are OUTSIDE this wrapper so they never move
//       cloneTable.style.transform = `translateX(-${scrollLeft}px)`;

//       const cloneThs = cloneTable.querySelectorAll(
//         "th",
//       ) as NodeListOf<HTMLElement>;
//       const originalThs = containerRef.current.querySelectorAll(
//         ".p-datatable-thead th",
//       ) as NodeListOf<HTMLElement>;

//       cloneThs.forEach((cloneTh, i) => {
//         const originalTh = originalThs[i] as HTMLElement;
//         if (!originalTh) return;

//         const borderColor = thStyleOverrides.borderColor || "#e5e7eb";

//         if (isFrozenTh(originalTh)) {
//           // Counter-translate frozen columns — pin them back
//           cloneTh.style.transform = `translateX(${scrollLeft}px)`;
//           cloneTh.style.borderLeft = `1px solid ${borderColor}`;
//           cloneTh.style.borderTop = `1px solid ${borderColor}`;
//           cloneTh.style.borderBottom = `2px solid ${borderColor}`;
//           cloneTh.style.backgroundColor =
//             thStyleOverrides.backgroundColor || "linear-gradient(#ffffff,#f6f7f8)"
//           cloneTh.style.zIndex = "20";
//           cloneTh.style.position = "sticky";
//         } else {
//           cloneTh.style.transform = "";
//           cloneTh.style.borderLeft = `1px solid ${borderColor}`;
//           cloneTh.style.borderTop = `1px solid ${borderColor}`;
//           cloneTh.style.borderBottom = `2px solid ${borderColor}`;
//         }
//       });
//     };

//     // ── Sort-icon / aria-sort mirroring ────────────────────────────────────
//     // Copies PrimeReact's sort state (icon class + direction + aria-sort)
//     // from the original (hidden) <th> onto the matching cloned <th>, so the
//     // sticky header visually reflects the current sort without needing its
//     // own sort state.
//     const syncSortVisuals = () => {
//       if (!stickyTable || !containerRef.current) return;
//       const cloneTable = stickyTable.querySelector("table") as HTMLElement;
//       if (!cloneTable) return;

//       const originalThs = containerRef.current.querySelectorAll(
//         ".p-datatable-thead th",
//       ) as NodeListOf<HTMLElement>;
//       const cloneThs = cloneTable.querySelectorAll(
//         "th",
//       ) as NodeListOf<HTMLElement>;

//       originalThs.forEach((originalTh, i) => {
//         const cloneTh = cloneThs[i] as HTMLElement;
//         if (!cloneTh) return;
//         if (!isSortableTh(originalTh)) return;

//         const ariaSort = originalTh.getAttribute("aria-sort");
//         if (ariaSort) {
//           cloneTh.setAttribute("aria-sort", ariaSort);
//         } else {
//           cloneTh.removeAttribute("aria-sort");
//         }

//         // PrimeReact toggles a sort-icon element's class/content based on
//         // direction. Copy its outerHTML onto the clone's matching icon so
//         // the arrow direction stays in sync without re-cloning the whole row.
//         const originalIcon = originalTh.querySelector(
//           ".p-sortable-column-icon",
//         ) as HTMLElement | null;
//         const cloneIcon = cloneTh.querySelector(
//           ".p-sortable-column-icon",
//         ) as HTMLElement | null;
//         if (originalIcon && cloneIcon) {
//           cloneIcon.className = originalIcon.className;
//           cloneIcon.innerHTML = originalIcon.innerHTML;
//         }

//         const originalBadge = originalTh.querySelector(
//           ".p-sortable-column-badge",
//         ) as HTMLElement | null;
//         const cloneBadge = cloneTh.querySelector(
//           ".p-sortable-column-badge",
//         ) as HTMLElement | null;
//         if (originalBadge && cloneBadge) {
//           cloneBadge.textContent = originalBadge.textContent;
//           cloneBadge.style.display = originalBadge.style.display;
//         }
//       });
//     };

//     const createStickyHeader = () => {
//       if (isRebuilding) return;
//       isRebuilding = true;

//       try {
//         if (!containerRef.current) return;
//         const originalTable = containerRef.current.querySelector(
//           ".p-datatable-table",
//         ) as HTMLElement;
//         const originalThead = containerRef.current.querySelector(
//           ".p-datatable-thead",
//         ) as HTMLElement;
//         const wrapper = containerRef.current.querySelector(
//           ".p-datatable-wrapper",
//         ) as HTMLElement;
//         if (!originalTable || !originalThead || !wrapper) return;

//         const existingSticky = document.querySelector(
//           "[data-sticky-table]",
//         ) as HTMLElement;

//         if (existingSticky) {
//           const existingAny = existingSticky as any;
//           if (existingAny._checkboxObserver) {
//             existingAny._checkboxObserver.disconnect();
//           }
//           if (existingAny._pollInterval) {
//             clearInterval(existingAny._pollInterval);
//           }
//           existingSticky.remove();
//         }

//         const containerRect = containerRef.current.getBoundingClientRect();

//         const fixedContainer = document.createElement("div");
//         fixedContainer.setAttribute("data-sticky-table", "true");
//         fixedContainer.style.cssText = `
// 					position: fixed;
// 					top: ${navbarHeight}px;
// 					left: ${containerRect.left}px;
// 					width: ${containerRect.width}px;
// 					overflow: hidden;
// 					z-index: 999;
// 					background: #ffffff;
// 				`;

//         const cloneTable = originalTable.cloneNode(true) as HTMLElement;

//         // ── KEY FIX: use separate borders instead of border-collapse ──────
//         // border-collapse causes shared borders that break when translateX is applied
//         cloneTable.style.cssText = `
// 					table-layout: fixed;
// 					width: ${originalTable.offsetWidth}px;
// 					margin: 0;
// 					border-collapse: separate;
// 					border-spacing: 0;
// 					transform: translateX(-${wrapper.scrollLeft}px);
// 				`;

//         cloneTable.querySelector(".p-datatable-tbody")?.remove();
//         cloneTable.querySelector(".p-datatable-tfoot")?.remove();

//         const originalThs = originalThead.querySelectorAll(
//           "th",
//         ) as NodeListOf<HTMLElement>;
//         const cloneThs = cloneTable.querySelectorAll(
//           "th",
//         ) as NodeListOf<HTMLElement>;

//         const borderColor = thStyleOverrides.borderColor || "#e5e7eb";

//         originalThs.forEach((th, i) => {
//           const cloneTh = cloneThs[i] as HTMLElement;
//           if (!cloneTh) return;

//           const width = th.getBoundingClientRect().width;
//           const computedStyle = window.getComputedStyle(th);
//           const computedBg = computedStyle.backgroundColor;
//           const frozen = isFrozenTh(th);
//           const sortable = isSortableTh(th);

//           cloneTh.style.width = `${width}px`;
//           cloneTh.style.minWidth = `${width}px`;
//           cloneTh.style.maxWidth = `${width}px`;
//           // ── Every th gets all 4 borders explicitly ────────────────────
//           cloneTh.style.borderTop = `1px solid ${borderColor}`;
//           cloneTh.style.borderBottom = `2px solid ${borderColor}`;
//           cloneTh.style.borderLeft = `1px solid ${borderColor}`;
//           cloneTh.style.boxSizing = "border-box";
//           cloneTh.style.backgroundColor =
//             thStyleOverrides.backgroundColor ||
//             (computedBg && computedBg !== "rgba(0, 0, 0, 0)"
//               ? computedBg
//               : "#ffffff");
//           cloneTh.style.color =
//             thStyleOverrides.color || computedStyle.color || "#0091b3";
//           cloneTh.style.fontWeight = thStyleOverrides.fontWeight || "bold";
//           cloneTh.style.fontSize =
//             thStyleOverrides.fontSize || computedStyle.fontSize || "0.9rem";
//           cloneTh.style.fontFamily = computedStyle.fontFamily;
//           cloneTh.style.textAlign = "left";
//           cloneTh.style.padding = thStyleOverrides.padding || "6px";
//           cloneTh.style.whiteSpace = "nowrap";
//           cloneTh.style.overflow = "hidden";
//           cloneTh.style.textOverflow = "ellipsis";
//           cloneTh.style.verticalAlign = "middle";

//           if (sortable) {
//             cloneTh.style.cursor = "pointer";
//           }

//           if (frozen) {
//             cloneTh.style.position = "sticky";
//             cloneTh.style.left = th.style.left || "0px";
//             cloneTh.style.zIndex = "20";
//             cloneTh.style.backgroundColor =
//               thStyleOverrides.backgroundColor || "linear-gradient(#ffffff,#f6f7f8)"
//           } else {
//             cloneTh.style.position = "";
//             cloneTh.style.left = "";
//             cloneTh.style.zIndex = "1";
//           }

//           const headerContent = cloneTh.querySelector(
//             ".p-column-header-content",
//           ) as HTMLElement;
//           if (headerContent) {
//             headerContent.style.display = "flex";
//             headerContent.style.alignItems = "center";
//             headerContent.style.color =
//               thStyleOverrides.color || computedStyle.color || "#0091b3";
//             headerContent.style.fontWeight =
//               thStyleOverrides.fontWeight || "bold";
//           }

//           const titleSpan = cloneTh.querySelector(
//             ".p-column-title",
//           ) as HTMLElement;
//           const originalTitleSpan = th.querySelector(
//             ".p-column-title",
//           ) as HTMLElement;

//           if (titleSpan && originalTitleSpan) {
//             titleSpan.style.color =
//               thStyleOverrides.color || computedStyle.color || "#0091b3";
//             titleSpan.style.fontWeight = thStyleOverrides.fontWeight || "bold";
//             titleSpan.style.fontSize =
//               thStyleOverrides.fontSize || computedStyle.fontSize || "0.9rem";
//             titleSpan.style.whiteSpace = "nowrap";
//             titleSpan.style.display = "inline-block";
//             titleSpan.style.maxWidth = `${headerTitleMaxWidth}px`;
//             titleSpan.style.overflow = "hidden";
//             titleSpan.style.textOverflow = "ellipsis";

//             // measure natural width from the original (already in DOM)
//             const prevMaxWidth = originalTitleSpan.style.maxWidth;
//             originalTitleSpan.style.maxWidth = "none";
//             const naturalWidth = originalTitleSpan.scrollWidth;
//             originalTitleSpan.style.maxWidth = prevMaxWidth;

//             // only show tooltip if truncated
//             cloneTh.title =
//               naturalWidth > headerTitleMaxWidth
//                 ? titleSpan.textContent || ""
//                 : "";
//           }

//           // ── Sort click passthrough ───────────────────────────────────
//           // The clone is a deep DOM clone, so it has no React listeners.
//           // Forward clicks to the matching original <th> so PrimeReact's
//           // real onSort handler runs; the resulting icon/aria-sort change
//           // gets mirrored back by syncSortVisuals() via the MutationObserver.
//           if (sortable) {
//             cloneTh.addEventListener("click", (e) => {
//               e.preventDefault();
//               e.stopPropagation();
//               const targetContent = th.querySelector(
//                 ".p-column-header-content",
//               ) as HTMLElement | null;
//               const clickTarget = targetContent || th;
//               clickTarget.dispatchEvent(
//                 new MouseEvent("click", {
//                   bubbles: true,
//                   cancelable: true,
//                   view: window,
//                 }),
//               );
//             });
//           }
//         });

//         // ── Selection column (optional) ───────────────────────────────────
//         const cloneSelectionTh = selectionHeaderSelector
//           ? (cloneTable.querySelector(
//               selectionHeaderSelector,
//             ) as HTMLElement | null)
//           : null;

//         if (cloneSelectionTh) {
//           cloneSelectionTh.style.width = `${selectionColumnWidth}px`;
//           cloneSelectionTh.style.minWidth = `${selectionColumnWidth}px`;
//           cloneSelectionTh.style.maxWidth = `${selectionColumnWidth}px`;
//           cloneSelectionTh.style.textAlign = "center";
//           cloneSelectionTh.style.padding = "0";
//           cloneSelectionTh.style.position = "sticky";
//           cloneSelectionTh.style.left = "0px";
//           cloneSelectionTh.style.zIndex = "20";
//           cloneSelectionTh.style.backgroundColor =
//             thStyleOverrides.backgroundColor || "linear-gradient(#ffffff,#f6f7f8)"
//           cloneSelectionTh.style.borderLeft = `1px solid ${borderColor}`;
//           cloneSelectionTh.style.borderTop = `1px solid ${borderColor}`;
//           cloneSelectionTh.style.borderBottom = `2px solid ${borderColor}`;

//           const headerContent = cloneSelectionTh.querySelector(
//             ".p-column-header-content",
//           ) as HTMLElement;
//           if (headerContent) {
//             headerContent.style.display = "flex";
//             headerContent.style.justifyContent = "center";
//             headerContent.style.alignItems = "center";
//             headerContent.style.width = "100%";
//             headerContent.style.height = "100%";
//             headerContent.style.padding = "0";
//           }

//           cloneSelectionTh.querySelectorAll("*").forEach((el: any) => {
//             el.style.pointerEvents = "none";
//           });

//           const overlay = document.createElement("div");
//           overlay.style.cssText = `
// 						position: absolute;
// 						top: 0; left: 0;
// 						width: 100%; height: 100%;
// 						cursor: pointer;
// 						z-index: 30;
// 						background: transparent;
// 					`;

//           overlay.addEventListener("click", (e) => {
//             e.preventDefault();
//             e.stopPropagation();
//             const originalInput = originalThead.querySelector(
//               `${selectionHeaderSelector} input[type="checkbox"]`,
//             ) as HTMLInputElement;
//             if (originalInput) {
//               originalInput.dispatchEvent(
//                 new MouseEvent("mousedown", {
//                   bubbles: true,
//                   cancelable: true,
//                   view: window,
//                 }),
//               );
//               originalInput.dispatchEvent(
//                 new MouseEvent("mouseup", {
//                   bubbles: true,
//                   cancelable: true,
//                   view: window,
//                 }),
//               );
//               originalInput.dispatchEvent(
//                 new MouseEvent("click", {
//                   bubbles: true,
//                   cancelable: true,
//                   view: window,
//                 }),
//               );
//             }
//             const originalBox = originalThead.querySelector(
//               `${selectionHeaderSelector} .p-checkbox-box`,
//             ) as HTMLElement;
//             if (originalBox) {
//               originalBox.dispatchEvent(
//                 new MouseEvent("click", {
//                   bubbles: true,
//                   cancelable: true,
//                   view: window,
//                 }),
//               );
//             }
//             const originalWrapper = originalThead.querySelector(
//               `${selectionHeaderSelector} .p-checkbox`,
//             ) as HTMLElement;
//             if (originalWrapper) {
//               originalWrapper.dispatchEvent(
//                 new MouseEvent("click", {
//                   bubbles: true,
//                   cancelable: true,
//                   view: window,
//                 }),
//               );
//             }
//             // NOTE: no manual handleScroll()/setTimeout/rAF refresh here.
//             // Forcing a refresh from inside this click handler raced with
//             // the MutationObserver-driven refresh that the resulting React
//             // re-render already triggers, and the two rebuilds tearing each
//             // other down mid-clone is what produced a blank cloned header.
//             // The existing checkboxObserver (visual sync) and the
//             // structural tableObserver (full rebuild) are sufficient.
//           });

//           cloneSelectionTh.appendChild(overlay);
//         }

//         // ── Wrap in a div tagged for clip reference ─────────────────────────
//         const clipWrapper = document.createElement("div");
//         clipWrapper.setAttribute("data-clip-wrapper", "true");
//         clipWrapper.style.cssText = `
// 					position: relative;
// 					overflow: hidden;
// 					width: 100%;
// 				`;
//         clipWrapper.appendChild(cloneTable);
//         fixedContainer.appendChild(clipWrapper);
//         document.body.appendChild(fixedContainer);
//         stickyTable = fixedContainer;

//         // Hide original thead
//         originalThead.style.visibility = "hidden";
//         originalThead.style.opacity = "0";
//         originalThead.style.pointerEvents = "none";

//         // ── Checkbox visual sync (only if there's a selection column) ──────
//         if (cloneSelectionTh && selectionHeaderSelector) {
//           const cloneCheckboxWrapper = cloneSelectionTh.querySelector(
//             ".p-checkbox",
//           ) as HTMLElement;
//           const cloneCheckboxBox = cloneSelectionTh.querySelector(
//             ".p-checkbox-box",
//           ) as HTMLElement;

//           const syncCheckboxVisual = () => {
//             if (
//               !containerRef.current ||
//               !cloneCheckboxWrapper ||
//               !cloneCheckboxBox
//             )
//               return;

//             const allRows = containerRef.current.querySelectorAll(
//               ".p-datatable-tbody .p-checkbox",
//             );
//             const highlightedRows = containerRef.current.querySelectorAll(
//               ".p-datatable-tbody .p-checkbox.p-highlight",
//             );
//             const total = allRows.length;
//             const checked = highlightedRows.length;

//             if (total === 0 || checked === 0) {
//               cloneCheckboxWrapper.classList.remove("p-highlight");
//               cloneCheckboxBox.classList.remove("p-highlight");
//               cloneCheckboxBox.style.backgroundColor = "";
//               cloneCheckboxBox.style.borderColor = "";
//               cloneCheckboxBox.innerHTML = "";
//             } else if (checked === total) {
//               cloneCheckboxWrapper.classList.add("p-highlight");
//               cloneCheckboxBox.classList.add("p-highlight");
//               cloneCheckboxBox.style.backgroundColor = "#6366f1";
//               cloneCheckboxBox.style.borderColor = "#6366f1";
//               cloneCheckboxBox.innerHTML = `
// 								<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:14px;height:14px;display:block;">
// 									<path d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
// 										stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
// 								</svg>
// 							`;
//             } else {
//               cloneCheckboxWrapper.classList.remove("p-highlight");
//               cloneCheckboxBox.classList.remove("p-highlight");
//               cloneCheckboxBox.style.backgroundColor = "";
//               cloneCheckboxBox.style.borderColor = "";
//               cloneCheckboxBox.innerHTML = "";
//             }
//           };

//           syncCheckboxVisual();
//           const pollInterval = setInterval(syncCheckboxVisual, 80);
//           (stickyTable as any)._pollInterval = pollInterval;

//           const tbody = containerRef.current?.querySelector(
//             ".p-datatable-tbody",
//           ) as HTMLElement;
//           const checkboxObserver = new MutationObserver(() => {
//             requestAnimationFrame(syncCheckboxVisual);
//           });
//           if (tbody) {
//             checkboxObserver.observe(tbody, {
//               attributes: true,
//               attributeFilter: ["class"],
//               subtree: true,
//             });
//           }
//           (stickyTable as any)._checkboxObserver = checkboxObserver;
//         }

//         // ── Sort visual sync ────────────────────────────────────────────
//         // Mirrors aria-sort / sort-icon state from the original header onto
//         // the clone whenever the original changes (e.g. after a sort click
//         // forwarded above causes PrimeReact to re-render its real header).
//         syncSortVisuals();
//         const sortObserver = new MutationObserver(() => {
//           requestAnimationFrame(syncSortVisuals);
//         });
//         sortObserver.observe(originalThead, {
//           attributes: true,
//           attributeFilter: ["aria-sort", "class"],
//           subtree: true,
//         });
//         (stickyTable as any)._sortObserver = sortObserver;

//         wrapper.addEventListener("scroll", syncHorizontalScroll, {
//           passive: true,
//         });
//       } finally {
//         isRebuilding = false;
//       }
//     };

//     const removeSticky = () => {
//       const existing = document.querySelector("[data-sticky-table]") as any;
//       if (existing?._checkboxObserver) existing._checkboxObserver.disconnect();
//       if (existing?._sortObserver) existing._sortObserver.disconnect();
//       if (existing?._pollInterval) clearInterval(existing._pollInterval);
//       document.querySelector("[data-sticky-table]")?.remove();
//       stickyTable = null;
//       if (!containerRef.current) return;
//       const originalThead = containerRef.current.querySelector(
//         ".p-datatable-thead",
//       ) as HTMLElement;
//       if (originalThead) {
//         originalThead.style.visibility = "visible";
//         originalThead.style.opacity = "1";
//         originalThead.style.pointerEvents = "";
//       }
//       const wrapper = containerRef.current.querySelector(
//         ".p-datatable-wrapper",
//       ) as HTMLElement;
//       wrapper?.removeEventListener("scroll", syncHorizontalScroll);
//     };

//     const refreshStickyHeader = () => {
//       if (!stickyTable || isRebuilding) return;

//       removeSticky();

//       requestAnimationFrame(() => {
//         handleScroll();
//       });
//     };

//     const handleScroll = () => {
//       if (!containerRef.current) return;
//       if (isRebuilding) return;

//       const originalThead = containerRef.current.querySelector(
//         ".p-datatable-thead",
//       ) as HTMLElement;

//       if (!originalThead) return;

//       const tableRect = containerRef.current.getBoundingClientRect();

//       const shouldShowSticky =
//         tableRect.top <= navbarHeight && tableRect.bottom > navbarHeight;

//       if (shouldShowSticky) {
//         if (!stickyTable) {
//           createStickyHeader();
//         } else {
//           const containerRect = containerRef.current.getBoundingClientRect();

//           stickyTable.style.left = `${containerRect.left}px`;
//           stickyTable.style.width = `${containerRect.width}px`;

//           syncHorizontalScroll();
//         }
//       } else {
//         if (stickyTable) {
//           removeSticky();
//         }
//       }
//     };

//     const handleResize = () => {
//       if (!stickyTable || isRebuilding) return;

//       removeSticky();

//       requestAnimationFrame(() => {
//         handleScroll();
//       });
//     };

//     let tableObserver: MutationObserver | null = null;

//     if (containerRef.current) {
//       let refreshTimer: number | null = null;
//       tableObserver = new MutationObserver(() => {
//         if (refreshTimer) {
//           clearTimeout(refreshTimer);
//         }

//         refreshTimer = window.setTimeout(() => {
//           if (stickyTable && !isRebuilding) {
//             removeSticky();

//             requestAnimationFrame(() => {
//               handleScroll();
//             });
//           }
//         }, 50);
//       });

//       tableObserver.observe(containerRef.current, {
//         childList: true,
//         subtree: true,
//       });
//     }
//     window.addEventListener("scroll", handleScroll, { passive: true });
//     window.addEventListener("resize", handleResize);

//     requestAnimationFrame(() => {
//       handleScroll();
//     });

//     // Tooltip for truncated header titles on the (non-sticky) original header
//     if (containerRef.current) {
//       const originalThs = containerRef.current.querySelectorAll(
//         ".p-datatable-thead > tr > th",
//       ) as NodeListOf<HTMLElement>;

//       originalThs.forEach((th) => {
//         const titleSpan = th.querySelector(".p-column-title") as HTMLElement;
//         if (titleSpan) {
//           const prevMaxWidth = titleSpan.style.maxWidth;
//           titleSpan.style.maxWidth = "none";
//           const naturalWidth = titleSpan.scrollWidth;
//           titleSpan.style.maxWidth = prevMaxWidth;

//           th.title =
//             naturalWidth > headerTitleMaxWidth
//               ? titleSpan.textContent || ""
//               : "";
//         }
//       });
//     }

//     return () => {
//       tableObserver?.disconnect();

//       window.removeEventListener("scroll", handleScroll);
//       window.removeEventListener("resize", handleResize);

//       removeSticky();
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [...watchDeps, enabled]);

//   return containerRef;
// }

import { useEffect, useRef } from "react";

export interface StickyHeaderThStyleOverrides {
	color?: string;
	fontWeight?: string;
	fontSize?: string;
	borderColor?: string;
	backgroundColor?: string;
	padding?: string;
}

export interface UseStickyDataTableHeaderOptions {
	navbarHeight?: number;
	frozenColumnSelector?: string;
	selectionHeaderSelector?: string | null;
	selectionColumnWidth?: number;
	headerTitleMaxWidth?: number;
	thStyleOverrides?: StickyHeaderThStyleOverrides;
	enabled?: boolean;
	sortableColumnSelector?: string;
}

const DEFAULT_FROZEN_SELECTOR = ".p-frozen-column, .selectionHeader";
const DEFAULT_SELECTION_SELECTOR = ".selectionHeader";
const DEFAULT_NAVBAR_HEIGHT = 112;
const DEFAULT_HEADER_TITLE_MAX_WIDTH = 120;
const DEFAULT_SORTABLE_SELECTOR = ".p-sortable-column";

/**
 * v3 — complete rewrite of the two broken subsystems:
 *
 * BUG 1 (header vanishes on select-all / any row selection):
 *   PrimeReact's "select all" causes React to re-render the DataTable, which
 *   replaces the <tbody> DOM node entirely. That is a structural childList
 *   mutation on the <table>, so it passed every filter we tried and triggered
 *   removeSticky() → rebuild, blanking the header during the gap.
 *   NEW APPROACH: drop the structural tableObserver entirely. Instead, the
 *   watchDeps array (columns, rows) passed by the consumer already rebuilds
 *   the hook via useEffect when the table data changes. For position/size
 *   updates we use a ResizeObserver on the container. Row selection does not
 *   change columns or layout, so it never triggers a rebuild — the header
 *   stays intact. The checkboxObserver inside the sticky header handles the
 *   visual checkbox sync independently.
 *
 * BUG 2 (horizontal scroll does not sync):
 *   Previously we attached the scroll listener to the wrapper node captured
 *   at build time. After a re-render PrimeReact can replace that node,
 *   orphaning the listener. Also the clip wrapper had overflow:hidden which
 *   prevented translateX from revealing non-frozen columns.
 *   NEW APPROACH: attach ONE scroll listener to window (capture phase) and
 *   re-query the wrapper fresh on every tick. Remove the clip wrapper's
 *   overflow constraint so translateX on the clone table works freely; the
 *   outer fixedContainer's overflow:hidden is the only clip boundary.
 */
export function useStickyDataTableHeader<
	T extends HTMLElement = HTMLDivElement,
>(
	watchDeps: React.DependencyList,
	options: UseStickyDataTableHeaderOptions = {},
) {
	const containerRef = useRef<T>(null);

	const {
		navbarHeight = DEFAULT_NAVBAR_HEIGHT,
		frozenColumnSelector = DEFAULT_FROZEN_SELECTOR,
		selectionHeaderSelector = DEFAULT_SELECTION_SELECTOR,
		selectionColumnWidth = 35,
		headerTitleMaxWidth = DEFAULT_HEADER_TITLE_MAX_WIDTH,
		thStyleOverrides = {},
		enabled = true,
		sortableColumnSelector = DEFAULT_SORTABLE_SELECTOR,
	} = options;

	useEffect(() => {
		if (!enabled) return;

		let stickyTable: HTMLElement | null = null;
		let isRebuilding = false;

		const isFrozenTh = (th: HTMLElement) => th.matches(frozenColumnSelector);
		const isSortableTh = (th: HTMLElement) =>
			sortableColumnSelector ? th.matches(sortableColumnSelector) : false;

		// ─────────────────────────────────────────────────────────────────────
		// HORIZONTAL SCROLL SYNC
		// Always re-queries the wrapper so stale node references never break it.
		// The fixedContainer clips via overflow:hidden; the inner table slides
		// with translateX to reveal non-frozen columns as the user scrolls.
		// Frozen columns counter-translate to stay pinned.
		// ─────────────────────────────────────────────────────────────────────
		const syncHorizontalScroll = () => {
			if (!stickyTable || !containerRef.current) return;

			const wrapper = containerRef.current.querySelector(
				".p-datatable-wrapper",
			) as HTMLElement | null;
			const cloneTable = stickyTable.querySelector(
				"table",
			) as HTMLElement | null;
			if (!wrapper || !cloneTable) return;

			const scrollLeft = wrapper.scrollLeft;
			const borderColor = thStyleOverrides.borderColor || "#e5e7eb";

			// Slide the entire clone table left to match horizontal scroll
			cloneTable.style.transform = `translateX(-${scrollLeft}px)`;

			const cloneThs = cloneTable.querySelectorAll(
				"th",
			) as NodeListOf<HTMLElement>;
			const originalThs = containerRef.current.querySelectorAll(
				".p-datatable-thead th",
			) as NodeListOf<HTMLElement>;

			cloneThs.forEach((cloneTh, i) => {
				const originalTh = originalThs[i] as HTMLElement | undefined;
				if (!originalTh) return;

				if (isFrozenTh(originalTh)) {
					// Counter-translate frozen columns so they stay pinned
					cloneTh.style.transform = `translateX(${scrollLeft}px)`;
					cloneTh.style.borderLeft = `1px solid ${borderColor}`;
					cloneTh.style.borderTop = `1px solid ${borderColor}`;
					cloneTh.style.borderBottom = `2px solid ${borderColor}`;
					cloneTh.style.backgroundColor =
						thStyleOverrides.backgroundColor ||
						"linear-gradient(#ffffff,#f6f7f8)";

					cloneTh.style.zIndex = "20";
					cloneTh.style.position = "sticky";
				} else {
					cloneTh.style.transform = "";
					cloneTh.style.borderLeft = `1px solid ${borderColor}`;
					cloneTh.style.borderTop = `1px solid ${borderColor}`;
					cloneTh.style.borderBottom = `2px solid ${borderColor}`;
				}
			});
		};

		// ─────────────────────────────────────────────────────────────────────
		// SORT ICON / ARIA-SORT MIRRORING
		// ─────────────────────────────────────────────────────────────────────
		const syncSortVisuals = () => {
			if (!stickyTable || !containerRef.current) return;
			const cloneTable = stickyTable.querySelector("table") as HTMLElement;
			if (!cloneTable) return;

			const originalThs = containerRef.current.querySelectorAll(
				".p-datatable-thead th",
			) as NodeListOf<HTMLElement>;
			const cloneThs = cloneTable.querySelectorAll(
				"th",
			) as NodeListOf<HTMLElement>;

			originalThs.forEach((originalTh, i) => {
				const cloneTh = cloneThs[i] as HTMLElement | undefined;
				if (!cloneTh || !isSortableTh(originalTh)) return;

				const ariaSort = originalTh.getAttribute("aria-sort");
				if (ariaSort) cloneTh.setAttribute("aria-sort", ariaSort);
				else cloneTh.removeAttribute("aria-sort");

				const originalIcon = originalTh.querySelector(
					".p-sortable-column-icon",
				) as HTMLElement | null;
				const cloneIcon = cloneTh.querySelector(
					".p-sortable-column-icon",
				) as HTMLElement | null;
				if (originalIcon && cloneIcon) {
					cloneIcon.className = originalIcon.className;
					cloneIcon.innerHTML = originalIcon.innerHTML;
				}

				const originalBadge = originalTh.querySelector(
					".p-sortable-column-badge",
				) as HTMLElement | null;
				const cloneBadge = cloneTh.querySelector(
					".p-sortable-column-badge",
				) as HTMLElement | null;
				if (originalBadge && cloneBadge) {
					cloneBadge.textContent = originalBadge.textContent;
					cloneBadge.style.display = originalBadge.style.display;
				}
			});
		};

		// ─────────────────────────────────────────────────────────────────────
		// REMOVE STICKY HEADER
		// ─────────────────────────────────────────────────────────────────────
		const removeSticky = () => {
			const existing = document.querySelector("[data-sticky-table]") as any;
			if (existing?._checkboxObserver) existing._checkboxObserver.disconnect();
			if (existing?._sortObserver) existing._sortObserver.disconnect();
			if (existing?._pollInterval) clearInterval(existing._pollInterval);
			document.querySelector("[data-sticky-table]")?.remove();
			stickyTable = null;

			if (!containerRef.current) return;
			const thead = containerRef.current.querySelector(
				".p-datatable-thead",
			) as HTMLElement | null;
			if (thead) {
				thead.style.visibility = "visible";
				thead.style.opacity = "1";
				thead.style.pointerEvents = "";
			}
		};

		// ─────────────────────────────────────────────────────────────────────
		// CREATE STICKY HEADER
		// ─────────────────────────────────────────────────────────────────────
		const createStickyHeader = () => {
			if (isRebuilding) return;
			isRebuilding = true;

			try {
				if (!containerRef.current) return;

				const originalTable = containerRef.current.querySelector(
					".p-datatable-table",
				) as HTMLElement | null;
				const originalThead = containerRef.current.querySelector(
					".p-datatable-thead",
				) as HTMLElement | null;
				const wrapper = containerRef.current.querySelector(
					".p-datatable-wrapper",
				) as HTMLElement | null;
				if (!originalTable || !originalThead || !wrapper) return;

				// Clean up any existing sticky header first
				removeSticky();

				const containerRect = containerRef.current.getBoundingClientRect();

				// ── Fixed outer container ─────────────────────────────────────────
				// overflow:hidden here is the clip boundary for horizontal scroll.
				// There is NO inner clip wrapper — that was causing translateX to be
				// double-clipped, breaking horizontal scroll sync.
				const fixedContainer = document.createElement("div");
				fixedContainer.setAttribute("data-sticky-table", "true");
				fixedContainer.style.cssText = `
          position: fixed;
          top: ${navbarHeight}px;
          left: ${containerRect.left}px;
          width: ${containerRect.width}px;
          overflow: hidden;
          z-index: 999;
          background: #ffffff;
        `;

				// ── Clone the table (thead only) ──────────────────────────────────
				const cloneTable = originalTable.cloneNode(true) as HTMLElement;
				cloneTable.style.cssText = `
          table-layout: fixed;
          width: ${originalTable.offsetWidth}px;
          margin: 0;
          border-collapse: separate;
          border-spacing: 0;
          transform: translateX(-${wrapper.scrollLeft}px);
        `;
				cloneTable.querySelector(".p-datatable-tbody")?.remove();
				cloneTable.querySelector(".p-datatable-tfoot")?.remove();

				const originalThs = originalThead.querySelectorAll(
					"th",
				) as NodeListOf<HTMLElement>;
				const cloneThs = cloneTable.querySelectorAll(
					"th",
				) as NodeListOf<HTMLElement>;

				const borderColor = thStyleOverrides.borderColor || "#e5e7eb";

				originalThs.forEach((th, i) => {
					const cloneTh = cloneThs[i] as HTMLElement | undefined;
					if (!cloneTh) return;

					const width = th.getBoundingClientRect().width;
					const cs = window.getComputedStyle(th);
					const computedBg = cs.backgroundColor;
					const frozen = isFrozenTh(th);
					const sortable = isSortableTh(th);

					// ── Size & borders ──────────────────────────────────────────────
					cloneTh.style.width = `${width}px`;
					cloneTh.style.minWidth = `${width}px`;
					cloneTh.style.maxWidth = `${width}px`;
					cloneTh.style.borderTop = `1px solid ${borderColor}`;
					cloneTh.style.borderBottom = `2px solid ${borderColor}`;
					cloneTh.style.borderLeft = `1px solid ${borderColor}`;
					cloneTh.style.boxSizing = "border-box";

					// ── Colors & typography ─────────────────────────────────────────
					cloneTh.style.backgroundColor =
						thStyleOverrides.backgroundColor ||
						(computedBg && computedBg !== "rgba(0, 0, 0, 0)"
							? computedBg
							: "#ffffff");
					cloneTh.style.color = thStyleOverrides.color || cs.color || "#0091b3";
					cloneTh.style.fontWeight = thStyleOverrides.fontWeight || "bold";
					cloneTh.style.fontSize =
						thStyleOverrides.fontSize || cs.fontSize || "0.9rem";
					cloneTh.style.fontFamily = cs.fontFamily;
					cloneTh.style.textAlign = "left";
					cloneTh.style.padding = thStyleOverrides.padding || "6px";
					cloneTh.style.whiteSpace = "nowrap";
					cloneTh.style.overflow = "hidden";
					cloneTh.style.textOverflow = "ellipsis";
					cloneTh.style.verticalAlign = "middle";

					if (sortable) cloneTh.style.cursor = "pointer";

					// ── Frozen column pinning ───────────────────────────────────────
					if (frozen) {
						cloneTh.style.position = "sticky";
						cloneTh.style.left = th.style.left || "0px";
						cloneTh.style.zIndex = "20";
						cloneTh.style.backgroundColor =
							thStyleOverrides.backgroundColor ||
							"linear-gradient(#ffffff,#f6f7f8)";
					} else {
						cloneTh.style.position = "";
						cloneTh.style.left = "";
						cloneTh.style.zIndex = "1";
					}

					// ── Header content flex layout ──────────────────────────────────
					const headerContent = cloneTh.querySelector(
						".p-column-header-content",
					) as HTMLElement | null;
					if (headerContent) {
						headerContent.style.display = "flex";
						headerContent.style.alignItems = "center";
						headerContent.style.color =
							thStyleOverrides.color || cs.color || "#0091b3";
						headerContent.style.fontWeight =
							thStyleOverrides.fontWeight || "bold";
					}

					// ── Title span truncation + tooltip ────────────────────────────
					const titleSpan = cloneTh.querySelector(
						".p-column-title",
					) as HTMLElement | null;
					const originalTitleSpan = th.querySelector(
						".p-column-title",
					) as HTMLElement | null;

					if (titleSpan && originalTitleSpan) {
						titleSpan.style.color =
							thStyleOverrides.color || cs.color || "#0091b3";
						titleSpan.style.fontWeight = thStyleOverrides.fontWeight || "bold";
						titleSpan.style.fontSize =
							thStyleOverrides.fontSize || cs.fontSize || "0.9rem";
						titleSpan.style.whiteSpace = "nowrap";
						titleSpan.style.display = "inline-block";
						titleSpan.style.maxWidth = `${headerTitleMaxWidth}px`;
						titleSpan.style.overflow = "hidden";
						titleSpan.style.textOverflow = "ellipsis";

						const prevMaxWidth = originalTitleSpan.style.maxWidth;
						originalTitleSpan.style.maxWidth = "none";
						const naturalWidth = originalTitleSpan.scrollWidth;
						originalTitleSpan.style.maxWidth = prevMaxWidth;

						cloneTh.title =
							naturalWidth > headerTitleMaxWidth
								? titleSpan.textContent || ""
								: "";
					}

					// ── Sort click passthrough ──────────────────────────────────────
					if (sortable) {
						cloneTh.addEventListener("click", (e) => {
							e.preventDefault();
							e.stopPropagation();
							const target =
								(th.querySelector(
									".p-column-header-content",
								) as HTMLElement | null) || th;
							target.dispatchEvent(
								new MouseEvent("click", {
									bubbles: true,
									cancelable: true,
									view: window,
								}),
							);
						});
					}
				});

				// ── Selection column ──────────────────────────────────────────────
				const cloneSelectionTh = selectionHeaderSelector
					? (cloneTable.querySelector(
							selectionHeaderSelector,
						) as HTMLElement | null)
					: null;

				if (cloneSelectionTh) {
					cloneSelectionTh.style.width = `${selectionColumnWidth}px`;
					cloneSelectionTh.style.minWidth = `${selectionColumnWidth}px`;
					cloneSelectionTh.style.maxWidth = `${selectionColumnWidth}px`;
					cloneSelectionTh.style.textAlign = "center";
					cloneSelectionTh.style.padding = "0";
					cloneSelectionTh.style.position = "sticky";
					cloneSelectionTh.style.left = "0px";
					cloneSelectionTh.style.zIndex = "20";
					cloneSelectionTh.style.backgroundColor =
						thStyleOverrides.backgroundColor ||
						"linear-gradient(#ffffff,#f6f7f8)";
					cloneSelectionTh.style.borderLeft = `1px solid ${borderColor}`;
					cloneSelectionTh.style.borderTop = `1px solid ${borderColor}`;
					cloneSelectionTh.style.borderBottom = `2px solid ${borderColor}`;

					const hc = cloneSelectionTh.querySelector(
						".p-column-header-content",
					) as HTMLElement | null;
					if (hc) {
						hc.style.display = "flex";
						hc.style.justifyContent = "center";
						hc.style.alignItems = "center";
						hc.style.width = "100%";
						hc.style.height = "100%";
						hc.style.padding = "0";
					}

					// Disable pointer events on all children; overlay handles clicks
					cloneSelectionTh.querySelectorAll("*").forEach((el: any) => {
						el.style.pointerEvents = "none";
					});

					const overlay = document.createElement("div");
					overlay.style.cssText = `
            position: absolute;
            top: 0; left: 0;
            width: 100%; height: 100%;
            cursor: pointer;
            z-index: 30;
            background: transparent;
          `;
					overlay.addEventListener("click", (e) => {
						e.preventDefault();
						e.stopPropagation();
						// Forward click to the real (hidden) header checkbox
						const sel = selectionHeaderSelector!;
						const tryDispatch = (querySelector: string, events: string[]) => {
							const el = originalThead.querySelector(
								querySelector,
							) as HTMLElement | null;
							if (!el) return;
							events.forEach((evtName) =>
								el.dispatchEvent(
									new MouseEvent(evtName, {
										bubbles: true,
										cancelable: true,
										view: window,
									}),
								),
							);
						};
						tryDispatch(`${sel} input[type="checkbox"]`, [
							"mousedown",
							"mouseup",
							"click",
						]);
						tryDispatch(`${sel} .p-checkbox-box`, ["click"]);
						tryDispatch(`${sel} .p-checkbox`, ["click"]);
					});
					cloneSelectionTh.appendChild(overlay);
				}

				// ── Append clone table directly into fixedContainer ───────────────
				// No inner wrapper with overflow:hidden — that was the source of the
				// horizontal-scroll double-clip bug. The fixedContainer's own
				// overflow:hidden is sufficient to clip non-frozen scrolled columns.
				fixedContainer.appendChild(cloneTable);
				document.body.appendChild(fixedContainer);
				stickyTable = fixedContainer;

				// Hide original thead (still in DOM for layout, just invisible)
				originalThead.style.visibility = "hidden";
				originalThead.style.opacity = "0";
				originalThead.style.pointerEvents = "none";

				// ── Checkbox visual sync ──────────────────────────────────────────
				// Lightweight: only reads class state, never triggers a rebuild.
				if (cloneSelectionTh && selectionHeaderSelector) {
					const cloneCheckboxWrapper = cloneSelectionTh.querySelector(
						".p-checkbox",
					) as HTMLElement | null;
					const cloneCheckboxBox = cloneSelectionTh.querySelector(
						".p-checkbox-box",
					) as HTMLElement | null;

					const syncCheckboxVisual = () => {
						if (
							!containerRef.current ||
							!cloneCheckboxWrapper ||
							!cloneCheckboxBox
						)
							return;

						const allRows = containerRef.current.querySelectorAll(
							".p-datatable-tbody .p-checkbox",
						);
						const checkedRows = containerRef.current.querySelectorAll(
							".p-datatable-tbody .p-checkbox.p-highlight",
						);
						const total = allRows.length;
						const checked = checkedRows.length;

						if (total === 0 || checked === 0) {
							cloneCheckboxWrapper.classList.remove("p-highlight");
							cloneCheckboxBox.classList.remove("p-highlight");
							cloneCheckboxBox.style.backgroundColor = "";
							cloneCheckboxBox.style.borderColor = "";
							cloneCheckboxBox.innerHTML = "";
						} else if (checked === total) {
							cloneCheckboxWrapper.classList.add("p-highlight");
							cloneCheckboxBox.classList.add("p-highlight");
							cloneCheckboxBox.style.backgroundColor = "#6366f1";
							cloneCheckboxBox.style.borderColor = "#6366f1";
							cloneCheckboxBox.innerHTML = `
                <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"
                     style="width:14px;height:14px;display:block;">
                  <path d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                    stroke="white" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"/>
                </svg>`;
						} else {
							cloneCheckboxWrapper.classList.remove("p-highlight");
							cloneCheckboxBox.classList.remove("p-highlight");
							cloneCheckboxBox.style.backgroundColor = "";
							cloneCheckboxBox.style.borderColor = "";
							cloneCheckboxBox.innerHTML = "";
						}
					};

					syncCheckboxVisual();

					// Poll every 80 ms as a safety net
					const pollInterval = setInterval(syncCheckboxVisual, 80);
					(stickyTable as any)._pollInterval = pollInterval;

					// Also react immediately via MutationObserver on tbody class changes
					const tbody = containerRef.current?.querySelector(
						".p-datatable-tbody",
					) as HTMLElement | null;
					const checkboxObserver = new MutationObserver(() => {
						requestAnimationFrame(syncCheckboxVisual);
					});
					if (tbody) {
						checkboxObserver.observe(tbody, {
							attributes: true,
							attributeFilter: ["class"],
							subtree: true,
						});
					}
					(stickyTable as any)._checkboxObserver = checkboxObserver;
				}

				// ── Sort visual sync ──────────────────────────────────────────────
				syncSortVisuals();
				const sortObserver = new MutationObserver(() => {
					requestAnimationFrame(syncSortVisuals);
				});
				sortObserver.observe(originalThead, {
					attributes: true,
					attributeFilter: ["aria-sort", "class"],
					subtree: true,
				});
				(stickyTable as any)._sortObserver = sortObserver;

				// ── Initial horizontal scroll sync ────────────────────────────────
				syncHorizontalScroll();
			} finally {
				isRebuilding = false;
			}
		};

		// ─────────────────────────────────────────────────────────────────────
		// WINDOW SCROLL HANDLER
		// Decides whether to show / update / hide the sticky header.
		// ─────────────────────────────────────────────────────────────────────
		const handleScroll = () => {
			if (!containerRef.current || isRebuilding) return;

			const tableRect = containerRef.current.getBoundingClientRect();
			const shouldShowSticky =
				tableRect.top <= navbarHeight && tableRect.bottom > navbarHeight;

			if (shouldShowSticky) {
				if (!stickyTable) {
					createStickyHeader();
				} else {
					// Just reposition and sync scroll — no rebuild
					const containerRect = containerRef.current.getBoundingClientRect();
					stickyTable.style.left = `${containerRect.left}px`;
					stickyTable.style.width = `${containerRect.width}px`;
					syncHorizontalScroll();
				}
			} else {
				if (stickyTable) removeSticky();
			}
		};

		// ─────────────────────────────────────────────────────────────────────
		// WINDOW RESIZE HANDLER
		// ─────────────────────────────────────────────────────────────────────
		const handleResize = () => {
			if (!stickyTable || isRebuilding) return;
			removeSticky();
			requestAnimationFrame(handleScroll);
		};

		// ─────────────────────────────────────────────────────────────────────
		// HORIZONTAL SCROLL — wrapper listener
		// We add a dedicated listener on the datatable wrapper for horizontal
		// scroll events (which don't bubble to window). Re-query wrapper each
		// time so stale references after re-renders never cause silent failures.
		// ─────────────────────────────────────────────────────────────────────
		let currentWrapper: HTMLElement | null = null;

		const attachWrapperScrollListener = () => {
			if (!containerRef.current) return;
			const wrapper = containerRef.current.querySelector(
				".p-datatable-wrapper",
			) as HTMLElement | null;
			if (!wrapper || wrapper === currentWrapper) return;

			// Detach from old wrapper if it changed
			if (currentWrapper) {
				currentWrapper.removeEventListener("scroll", syncHorizontalScroll);
			}
			currentWrapper = wrapper;
			wrapper.addEventListener("scroll", syncHorizontalScroll, {
				passive: true,
			});
		};

		// ─────────────────────────────────────────────────────────────────────
		// RESIZE OBSERVER
		// Replaces the structural MutationObserver for detecting layout changes
		// (e.g. columns added/removed, table width changed). Does NOT fire on
		// row selection or class changes, so it never causes spurious rebuilds.
		// ─────────────────────────────────────────────────────────────────────
		let resizeObserver: ResizeObserver | null = null;

		if (containerRef.current && typeof ResizeObserver !== "undefined") {
			let resizeTimer: number | null = null;
			resizeObserver = new ResizeObserver(() => {
				if (resizeTimer) clearTimeout(resizeTimer);
				resizeTimer = window.setTimeout(() => {
					if (stickyTable && !isRebuilding) {
						removeSticky();
						requestAnimationFrame(handleScroll);
					}
					// Also re-attach wrapper scroll listener in case DOM changed
					attachWrapperScrollListener();
				}, 150);
			});
			resizeObserver.observe(containerRef.current);
		}

		// ─────────────────────────────────────────────────────────────────────
		// INIT
		// ─────────────────────────────────────────────────────────────────────
		window.addEventListener("scroll", handleScroll, { passive: true });
		window.addEventListener("resize", handleResize);
		attachWrapperScrollListener();

		requestAnimationFrame(handleScroll);

		// Tooltip for truncated titles on the real (visible) header
		if (containerRef.current) {
			containerRef.current
				.querySelectorAll(".p-datatable-thead > tr > th")
				.forEach((th) => {
					const titleSpan = (th as HTMLElement).querySelector(
						".p-column-title",
					) as HTMLElement | null;
					if (!titleSpan) return;
					const prev = titleSpan.style.maxWidth;
					titleSpan.style.maxWidth = "none";
					const natural = titleSpan.scrollWidth;
					titleSpan.style.maxWidth = prev;
					(th as HTMLElement).title =
						natural > headerTitleMaxWidth ? titleSpan.textContent || "" : "";
				});
		}

		return () => {
			resizeObserver?.disconnect();
			window.removeEventListener("scroll", handleScroll);
			window.removeEventListener("resize", handleResize);
			if (currentWrapper) {
				currentWrapper.removeEventListener("scroll", syncHorizontalScroll);
			}
			removeSticky();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...watchDeps, enabled]);

	return containerRef;
}
